/**
 * BUSCAR EN EL CATÁLOGO DE CULTIVOS Y VARIEDADES, EN MEMORIA
 * (01-10-2026, plan cultivos SIEX, F1 → lo usan los selectores de F3).
 *
 * El catálogo se carga UNA vez (`crop_catalog_list()`, ~1.120 cultivos) y las
 * variedades al elegir cultivo (`crop_catalog_varieties(code)`: el tomate trae
 * ~5.000). Buscar es síncrono y sin red: nada de una consulta por pulsación
 * (regla del repo y guard `registroSinRedPorPulsacion`). Con 5.000 variedades
 * una búsqueda tarda bastante menos de 10 ms (lo mide cropCatalogSearch.test).
 *
 * El orden, decidido en el plan (F3):
 *   1. el tipo de coincidencia: igual (en el idioma de la pantalla, el nombre
 *      oficial o un alias) > empieza por > empieza una palabra > contiene
 *      (3 letras o más) > nombre latino o código EPPO;
 *   2. los cultivos que ya usa la empresa;
 *   3. `greenhouse_rank` (los de invernadero, primero);
 *   4. hortícola;
 *   5. activos antes que dados de baja (y los de baja, ocultos salvo que sean
 *      el valor actual: un ciclo viejo no puede quedarse sin su cultivo);
 *   6. el nombre.
 * Sin texto: los de la empresa y luego los 12 curados de más arriba.
 *
 * PURO: sin red ni React.
 */
import { claveBusqueda } from "./clave";
import { CULTIVOS_CURADOS, type IdiomaCultivo } from "./curados";
import { estaDeBaja } from "./resolver";

/** Un cultivo tal como llega de `crop_catalog_list()` (claves de una letra: el jsonb viaja entero de una vez). */
export interface CultivoCatalogoItem {
  /** code: código SIEX. */
  c: number;
  /** name_es: el nombre castellano que se enseña. */
  n: string;
  /** name_official: el nombre SIEX tal cual. */
  o: string;
  /** latin. */
  l?: string | null;
  /** eppo. */
  e?: string | null;
  /** horticultural. */
  h?: boolean | null;
  /** siex_baja (ISO): dado de baja en SIEX desde esa fecha. */
  b?: string | null;
  /** curated_key: es de los curados. */
  k?: string | null;
  /** greenhouse_rank: 1 = el más habitual en invernadero. */
  r?: number | null;
  /** names_i18n: cómo se llama en cada idioma de la app. */
  i?: Partial<Record<IdiomaCultivo, string>> | null;
  /** Alias (sinónimos seguros: traducciones y plurales). */
  a?: string[] | null;
}

/** Una variedad tal como llega de `crop_catalog_varieties(code)`. */
export interface VariedadCatalogoItem {
  /** code de la variedad (único dentro de su cultivo). */
  c: number;
  /** name: el nombre SIEX tal cual (MAYÚSCULAS, D4). */
  n: string;
  /** siex_baja (ISO). */
  b?: string | null;
}

export type Coincidencia = "exacta" | "prefijo" | "palabra" | "contiene" | "latin" | "vacia";

export interface ResultadoCultivo {
  item: CultivoCatalogoItem;
  /** Lo que se enseña: el nombre en el idioma de la pantalla o, si no se sabe, el castellano. */
  etiqueta: string;
  coincidencia: Coincidencia;
  deBaja: boolean;
}

export interface ResultadoVariedad {
  item: VariedadCatalogoItem;
  coincidencia: Exclude<Coincidencia, "latin">;
  deBaja: boolean;
}

export interface OpcionesBuscarCultivos {
  lang?: IdiomaCultivo | string;
  /** Códigos que ya usa la empresa. */
  usados?: Iterable<number>;
  limit?: number;
  /** El valor actual del campo: se enseña aunque esté de baja. */
  actual?: number | null;
  /** Hoy en ISO; por defecto, hoy. */
  hoy?: string;
}

export interface OpcionesBuscarVariedades {
  /** Códigos de variedad que ya usa la empresa en este cultivo. */
  usadas?: Iterable<number>;
  limit?: number;
  actual?: number | null;
  hoy?: string;
}

/** Los curados que se enseñan sin texto. */
const TOP_CURADOS = 12;
const TIER: Record<Exclude<Coincidencia, "vacia">, number> = { exacta: 0, prefijo: 1, palabra: 2, contiene: 3, latin: 4 };

const hoyIso = () => new Date().toISOString().slice(0, 10);

/** Los nombres por los que se puede encontrar algo, ya en clave, con o sin espacio delante (para «empieza una palabra»). */
interface Claves {
  todas: string[];
  conEspacio: string[];
}

function claves(textos: Array<string | null | undefined>): Claves {
  const todas = [...new Set(textos.map((t) => claveBusqueda(t)).filter(Boolean))];
  return { todas, conEspacio: todas.map((k) => ` ${k}`) };
}

function coincide(q: string, c: Claves): Exclude<Coincidencia, "latin" | "vacia"> | null {
  let mejor: Exclude<Coincidencia, "latin" | "vacia"> | null = null;
  for (let i = 0; i < c.todas.length; i++) {
    const k = c.todas[i];
    if (k === q) return "exacta";
    if (k.startsWith(q)) mejor = "prefijo";
    else if (mejor !== "prefijo" && c.conEspacio[i].includes(` ${q}`)) mejor = "palabra";
    else if (!mejor && q.length >= 3 && k.includes(q)) mejor = "contiene";
  }
  return mejor;
}

// ── Cultivos ────────────────────────────────────────────────────────────────

interface CultivoPreparado {
  item: CultivoCatalogoItem;
  /** Oficial, castellano y alias: valen en cualquier idioma. */
  comunes: Claves;
  /** Por idioma de la pantalla: su etiqueta + los comunes. */
  porIdioma: Map<string, Claves>;
  latin: string;
  eppo: string;
}

const PREPARADOS = new WeakMap<readonly CultivoCatalogoItem[], CultivoPreparado[]>();

function prepararCultivos(items: readonly CultivoCatalogoItem[]): CultivoPreparado[] {
  let p = PREPARADOS.get(items);
  if (!p) {
    p = items.map((item) => ({
      item,
      comunes: claves([item.n, item.o, ...(item.a ?? [])]),
      porIdioma: new Map(),
      latin: claveBusqueda(item.l),
      eppo: (item.e ?? "").toLowerCase(),
    }));
    PREPARADOS.set(items, p);
  }
  return p;
}

function etiquetaDe(item: CultivoCatalogoItem, lang: string): string {
  return (lang !== "es" && item.i?.[lang as IdiomaCultivo]) || item.n;
}

function clavesEnIdioma(p: CultivoPreparado, lang: string): Claves {
  let c = p.porIdioma.get(lang);
  if (!c) {
    const propia = lang === "es" ? null : p.item.i?.[lang as IdiomaCultivo];
    c = propia ? claves([propia, ...p.comunes.todas]) : p.comunes;
    p.porIdioma.set(lang, c);
  }
  return c;
}

/** «pomodoro» (it) → 197 TOMATE primero; «tom» → Tomate antes que Tomate de Galápagos; «LYPES» → Tomate. */
export function buscarCultivos(
  items: readonly CultivoCatalogoItem[],
  q: string | null | undefined,
  opciones: OpcionesBuscarCultivos = {},
): ResultadoCultivo[] {
  const lang = String(opciones.lang ?? "es").split(/[-_]/)[0].toLowerCase();
  const limit = opciones.limit ?? 30;
  const hoy = opciones.hoy ?? hoyIso();
  const actual = opciones.actual ?? null;
  const usados = new Set(opciones.usados ?? []);
  const preparados = prepararCultivos(items);
  const qk = claveBusqueda(q);

  const visible = (it: CultivoCatalogoItem) => it.c === actual || !estaDeBaja(it.b, hoy);
  const resultado = (it: CultivoCatalogoItem, coincidencia: Coincidencia): ResultadoCultivo => ({
    item: it,
    etiqueta: etiquetaDe(it, lang),
    coincidencia,
    deBaja: estaDeBaja(it.b, hoy),
  });

  if (!qk) {
    // Sin texto: el valor actual, los de la empresa (por nombre) y los curados de más arriba.
    const fuera: ResultadoCultivo[] = [];
    const puestos = new Set<number>();
    const pon = (it: CultivoCatalogoItem) => {
      if (puestos.has(it.c) || !visible(it)) return;
      puestos.add(it.c);
      fuera.push(resultado(it, "vacia"));
    };
    const porCodigo = new Map(items.map((it) => [it.c, it]));
    if (actual != null && porCodigo.has(actual)) pon(porCodigo.get(actual)!);
    items
      .filter((it) => usados.has(it.c))
      .sort((x, y) => etiquetaDe(x, lang).localeCompare(etiquetaDe(y, lang), "es"))
      .forEach(pon);
    items
      .filter((it) => it.r != null)
      .sort((x, y) => (x.r as number) - (y.r as number))
      .slice(0, TOP_CURADOS)
      .forEach(pon);
    return fuera.slice(0, limit);
  }

  const casados: Array<{ p: CultivoPreparado; tier: number; coincidencia: Coincidencia; deBaja: boolean }> = [];
  for (const p of preparados) {
    if (!visible(p.item)) continue;
    let coincidencia: Coincidencia | null = coincide(qk, clavesEnIdioma(p, lang));
    if (!coincidencia && ((qk.length >= 3 && p.latin.includes(qk)) || (qk.length >= 3 && p.eppo.startsWith(qk)))) {
      coincidencia = "latin";
    }
    if (coincidencia) {
      casados.push({ p, tier: TIER[coincidencia as Exclude<Coincidencia, "vacia">], coincidencia, deBaja: estaDeBaja(p.item.b, hoy) });
    }
  }
  casados.sort((x, y) => {
    if (x.tier !== y.tier) return x.tier - y.tier;
    const ux = usados.has(x.p.item.c) ? 0 : 1;
    const uy = usados.has(y.p.item.c) ? 0 : 1;
    if (ux !== uy) return ux - uy;
    const rx = x.p.item.r ?? Number.POSITIVE_INFINITY;
    const ry = y.p.item.r ?? Number.POSITIVE_INFINITY;
    if (rx !== ry) return rx - ry;
    const hx = x.p.item.h ? 0 : 1;
    const hy = y.p.item.h ? 0 : 1;
    if (hx !== hy) return hx - hy;
    if (x.deBaja !== y.deBaja) return x.deBaja ? 1 : -1;
    return etiquetaDe(x.p.item, lang).localeCompare(etiquetaDe(y.p.item, lang), "es");
  });
  /* Si algo EMPIEZA por lo escrito, lo que solo casa a media frase (una palabra
     suelta, un trozo, el latín) entra solo si es hortícola, curado o de la
     empresa: «tom» daba al final «Híbridos de Prunus canescens y tomentosa».
     Si nada empieza por ahí, se enseña todo (quien busca «tomentosa» lo encuentra).
     Decisión de Pablo, 01-10-2026: «lo que tenga más sentido». */
  const hayPrefijo = casados.some((m) => m.tier <= TIER.prefijo);
  const utiles = hayPrefijo
    ? casados.filter((m) => m.tier <= TIER.prefijo || m.p.item.h || m.p.item.r != null || usados.has(m.p.item.c))
    : casados;
  return utiles.slice(0, limit).map((m) => ({ ...resultado(m.p.item, m.coincidencia), deBaja: m.deBaja }));
}

// ── Variedades ──────────────────────────────────────────────────────────────

interface VariedadPreparada {
  item: VariedadCatalogoItem;
  clave: string;
  conEspacio: string;
}

const PREPARADAS = new WeakMap<readonly VariedadCatalogoItem[], VariedadPreparada[]>();

function prepararVariedades(items: readonly VariedadCatalogoItem[]): VariedadPreparada[] {
  let p = PREPARADAS.get(items);
  if (!p) {
    p = items.map((item) => {
      const clave = claveBusqueda(item.n);
      return { item, clave, conEspacio: ` ${clave}` };
    });
    PREPARADAS.set(items, p);
  }
  return p;
}

/** «raf» → RAF (igual) antes que RAFAELLO (empieza por); sin texto: la actual, las de la empresa y el resto por nombre. */
export function buscarVariedades(
  items: readonly VariedadCatalogoItem[],
  q: string | null | undefined,
  opciones: OpcionesBuscarVariedades = {},
): ResultadoVariedad[] {
  const limit = opciones.limit ?? 50;
  const hoy = opciones.hoy ?? hoyIso();
  const actual = opciones.actual ?? null;
  const usadas = new Set(opciones.usadas ?? []);
  const qk = claveBusqueda(q);

  const casadas: Array<{ p: VariedadPreparada; tier: number; coincidencia: ResultadoVariedad["coincidencia"]; deBaja: boolean }> = [];
  for (const p of prepararVariedades(items)) {
    const deBaja = estaDeBaja(p.item.b, hoy);
    if (deBaja && p.item.c !== actual) continue;
    let coincidencia: ResultadoVariedad["coincidencia"] | null;
    if (!qk) coincidencia = "vacia";
    else if (p.clave === qk) coincidencia = "exacta";
    else if (p.clave.startsWith(qk)) coincidencia = "prefijo";
    else if (p.conEspacio.includes(` ${qk}`)) coincidencia = "palabra";
    else if (qk.length >= 3 && p.clave.includes(qk)) coincidencia = "contiene";
    else coincidencia = null;
    if (coincidencia) casadas.push({ p, tier: coincidencia === "vacia" ? 0 : TIER[coincidencia], coincidencia, deBaja });
  }
  casadas.sort((x, y) => {
    if (x.tier !== y.tier) return x.tier - y.tier;
    const ax = x.p.item.c === actual ? 0 : 1;
    const ay = y.p.item.c === actual ? 0 : 1;
    if (!qk && ax !== ay) return ax - ay;
    const ux = usadas.has(x.p.item.c) ? 0 : 1;
    const uy = usadas.has(y.p.item.c) ? 0 : 1;
    if (ux !== uy) return ux - uy;
    if (x.deBaja !== y.deBaja) return x.deBaja ? 1 : -1;
    return x.p.clave < y.p.clave ? -1 : x.p.clave > y.p.clave ? 1 : 0;
  });
  return casadas.slice(0, limit).map((m) => ({ item: m.p.item, coincidencia: m.coincidencia, deBaja: m.deBaja }));
}

// ── El catálogo mínimo sin red ──────────────────────────────────────────────

/**
 * Los cultivos curados en forma de item del catálogo: lo que enseña el selector
 * si `crop_catalog_list()` no responde (nunca se bloquea al agricultor), y la
 * base de los tests. Sin latín/EPPO/hortícola: eso solo lo sabe el catálogo.
 */
export const CATALOGO_CURADO: readonly CultivoCatalogoItem[] = CULTIVOS_CURADOS.map((c) => ({
  c: c.siex,
  n: c.visible,
  o: c.oficial,
  r: c.rango,
  k: c.i18nKey ?? claveBusqueda(c.visible),
  i: c.etiquetas ?? null,
  a: [...c.nombres],
}));
